import Overlay from "../utilities/overlay"
import { escListener, scrollLock, isInViewport, onWindowResize, removeOnWindowResize, debounce } from "../utilities/utils"
import { computePosition, offset, flip, shift, arrow, size } from "@floating-ui/dom"

const overlay = new Overlay()

class ToggletipEngine extends HTMLElement {
  constructor() {
    super()
    this.headerEl = document.getElementById("nav-wrapper")
    this.headerUXPosition = document.querySelector(".header").getAttribute("data-ux-position")

    this.predictiveSearch = this.querySelector("predictive-search")

    this.mobileView = this.dataset.mobileView ? this.dataset.mobileView : "floating"
    this.isWithinHeader = this.closest("#nav-wrapper")
    this.overlayAttachingElements = this.isWithinHeader
      ? [this.headerEl]
      : [this.closest(".shopify-section"), this.headerEl]

    this.navigationItemTrigger = this.querySelector("[data-tooltip-trigger]")
    this.navigationItemPopover = this.querySelector("[data-tooltip]")
    this.arrowElement = this.querySelector("#arrow")
    this.spacing = 16
    this.offset = this.getOffset()

    this.navigationItemPopover.addEventListener("click", (e) => {
      // stop propagation outside of the popover container
      e.stopPropagation()
    })

    this.closeOnScroll = this.closeOnScroll.bind(this)

    // Prevent clicks within toggletip from propagating to section
    this.addEventListener('click', (e) => {
      e.stopPropagation()
    })

    // Add click listener for section
    const section = this.closest('.shopify-section')
    if (section) {
      section.addEventListener('click', (event) => {
        // Check if click target is within a toggletip-engine
        const clickedToggletip = event.target.closest('toggletip-engine')
        if (!clickedToggletip) {
          closeAllToggletipsInSection(section)
        }
      })
    }

    this.handleResize = debounce(() => {
      if (this.isOpen) {
        const isMobile = window.matchMedia("(max-width: 1023px)").matches
        if (isMobile && this.mobileView === "fullscreen") {
          this.updateMobileFullscreen()
        } else {
          this.update()
        }
      }
    }, 100)

    this.container = this.closest('.shopify-section')?.querySelector('.section__wrapper')
  }

  getOffset() {
    const isMobile = window.matchMedia("(max-width: 1023px)").matches
    return isMobile ? 16 : 8
  }

  update() {
    const navigationItemTrigger = this.navigationItemTrigger
    const navigationItemPopover = this.navigationItemPopover
    const containerRect = this.container?.getBoundingClientRect()
    const isFullBleed = this.container?.classList.contains('container--full_bleed')

    // Get the container's computed padding
    const containerStyle = window.getComputedStyle(this.container)
    const containerPaddingLeft = parseInt(containerStyle.paddingLeft)
    const containerPaddingRight = parseInt(containerStyle.paddingRight)

    const middlewareOptions = [
      offset(this.getOffset()),
      flip({
        boundary: 'viewport',
        padding: {
          left: isFullBleed ? containerPaddingLeft : (containerRect?.left || 0),
          right: isFullBleed ? containerPaddingRight : (window.innerWidth - (containerRect?.right || window.innerWidth))
        }
      }),
      shift({
        boundary: 'viewport',
        padding: {
          left: isFullBleed ? containerPaddingLeft : (containerRect?.left || 0),
          right: isFullBleed ? containerPaddingRight : (window.innerWidth - (containerRect?.right || window.innerWidth))
        },
        alignment: 'start'
      }),
      arrow({ element: this.arrowElement }),
      size({
        apply({ availableWidth, availableHeight }) {
          const maxWidth = isFullBleed
            ? window.innerWidth - (containerPaddingLeft + containerPaddingRight)
            : (containerRect?.width > 1600 ? 1600 : containerRect?.width || Math.min(window.innerWidth, 1600))
          Object.assign(navigationItemPopover.style, {
            maxWidth: `${maxWidth}px`,
            maxHeight: navigationItemPopover.classList.contains('tooltip--mobile') ? '' : '640px',
            height: navigationItemPopover.classList.contains('tooltip--mobile') ? `${availableHeight}px` : '',
          })
        },
        padding: this.spacing,
        boundary: 'viewport'
      }),
    ]

    computePosition(navigationItemTrigger, navigationItemPopover, {
      placement: "bottom-start",
      middleware: middlewareOptions,
      boundary: 'viewport'
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(navigationItemPopover.style, {
        left: `${x}px`,
        top: `${y}px`,
      })

      if (!this.arrowElement) return

      // Accessing the data
      const { x: arrowX, y: arrowY } = middlewareData.arrow

      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[placement.split("-")[0]]

      Object.assign(this.arrowElement.style, {
        left: arrowX != null ? `${arrowX}px` : "",
        top: arrowY != null ? `${arrowY}px` : "",
        right: "",
        bottom: "",
        [staticSide]: `-${this.spacing}px`,
      })
    })
  }

  updateMobileFullscreen() {
    Object.assign(this.navigationItemPopover.style, {
      left: `${this.spacing}px`,
      top: `${this.spacing}px`,
      bottom: `${this.spacing}px`
    })
  }

  scrollIntoView(toggletip) {
    if (this.headerUXPosition !== "off") {
      const position = toggletip.getBoundingClientRect()
      window.scrollTo(position.left, position.top + window.scrollY - this.headerEl.clientHeight)
    } else {
      toggletip.scrollIntoView()
    }
  }

  open() {
    const isMobile = window.matchMedia("(max-width: 1023px)").matches

    // If already open, don't reopen
    if (this.isOpen) return;

    if (isMobile) {
      if (this.mobileView === "fullscreen") {
        if (this.headerUXPosition !== "off") {
          // hide header
          this.headerEl.style.marginTop = `-${this.headerEl.clientHeight}px`
          this.headerEl.props.lastScroll = window.scrollY
          this.headerEl.props.navMargin = this.headerEl.clientHeight
        }
        this.navigationItemPopover.removeAttribute("style")
        this.updateMobileFullscreen()
        overlay.open({
          attachElements: [this.closest(".shopify-section")],
        })
      } else {
        this.navigationItemPopover.removeAttribute("style")
        this.update()
        overlay.open({
          attachElements: [this.closest(".shopify-section"), this.headerEl],
        })
      }
      scrollLock(true)
    } else {
      this.navigationItemPopover.removeAttribute("style")
      this.update()
      overlay.open({
        attachElements: this.overlayAttachingElements,
      })

      if (!this.isWithinHeader) {
        setTimeout(() => {
          this.scrollIntoView(this.navigationItemPopover)
        }, 100)
      }

      document.addEventListener("scroll", this.closeOnScroll)
    }

    this.navigationItemTrigger.setAttribute("open", "true")

    overlay.onClick((e) => {
      this.close()
    })

    if (this.predictiveSearch) this.predictiveSearch.open()

    escListener(true, this.close.bind(this))

    window.addEventListener('resize', this.handleResize)
  }

  close(immediateDetach = false) {
    // If already closed, don't try to close again
    if (!this.isOpen) return;

    this.navigationItemTrigger.removeAttribute("open")

    Object.assign(this.navigationItemPopover.style, {
      top: '-9999px',
      left: '-9999px',
      visibility: 'hidden',
      opacity: '0',
      zIndex: '-1'
    });

    if (immediateDetach) {
      this.overlayAttachingElements.forEach((element) => {
        overlay.detachElement(element)
      })
    }

    overlay.close()

    if (this.predictiveSearch) this.predictiveSearch.close()

    escListener(false, this.close.bind(this))

    const isMobile = window.matchMedia("(max-width: 1023px)").matches

    if (isMobile) {
      scrollLock(false)
      this.headerEl.style.marginTop = `0px`
    } else {
      document.removeEventListener("scroll", this.closeOnScroll)
    }

    window.removeEventListener('resize', this.handleResize)
  }

  closeOnScroll() {
    const inViewport = isInViewport(this)

    if (!inViewport && this.isOpen) {
      this.close()
    }
  }

  get isOpen() {
    return this.navigationItemTrigger.getAttribute("open") === "true" && overlay.isOpen
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize)
    if (this.isOpen) this.close(true)
  }
}

if (!window.customElements.get("toggletip-engine")) {
  window.customElements.define("toggletip-engine", ToggletipEngine)
}

const closeAllToggletipsInSection = (section) => {
  const toggletips = section.querySelectorAll('toggletip-engine')
  toggletips.forEach(toggletip => toggletip.isOpen && toggletip.close())
}

const closeAllToggletips = (immediateDetach = false, exception = null) => {
  const toggletips = document.querySelectorAll("toggletip-engine")

  if (exception) {
    toggletips.forEach(
      (toggletip) =>
        toggletip !== exception && toggletip.isOpen && toggletip.close(immediateDetach),
    )
  } else {
    toggletips.forEach((toggletip) => toggletip.isOpen && toggletip.close(immediateDetach))
  }
}

export { closeAllToggletips, closeAllToggletipsInSection }
